// Bot defense for the PUBLIC web form (docs item 31).
//
// Two cheap checks, both aimed squarely at the dumb automated case:
//
//   1. A honeypot field — present in the DOM, invisible to a human, and never
//      touched by anyone filling the form by hand. Anything in it means a
//      script filled every input it could find.
//
//   2. A timing floor — a minimum plausible-human time from page load to
//      submit. A FLOOR ONLY, with NO upper limit, so a driver who starts the
//      form, gets interrupted, and submits twenty minutes later sails through.
//
// ⚠️ SCOPE — what this does NOT do. Both signals are supplied by the client and
// a determined attacker can forge them: post straight at /api/submit with a
// large elapsed value and no honeypot key and both checks pass. That is
// accepted and deliberate. This is a dumb-bot filter. The defense against a
// crafted payload is the server-side CONTENT rules (history_rules,
// dot_hours_rules), which judge the data itself and cannot be talked out of it.
//
// The mobile app is exempt by design — it has no page load and no browser
// session. See the item 31 notes in the Documentation repo.

// Field name. Chosen so browser autofill and password managers will not
// recognise it and helpfully type into it — that would be a false positive on
// a real driver. Do NOT rename this to anything resembling a real field
// (name, email, phone, address, company, fax, website).
export const HONEYPOT_FIELD = 'applicant_reference_code';

// Seconds. Robert timed straight data entry from a completed paper sheet at
// roughly FIVE MINUTES, so a real human is ~100x slower than this floor. It is
// deliberately not raised: the floor's only job is to catch something that is
// not reading the page at all, and that submits in well under a second. A
// script slow enough to beat 30s is slow enough to beat any number we pick, so
// a higher floor buys nothing real and only widens the false-positive surface.
export const MIN_FILL_SECONDS = 3;

// Monotonic clock reading, in milliseconds.
//
// 🔑 performance.now() ON PURPOSE, not Date.now(). It is monotonic and counts
// from page load, so it is immune to the device clock being corrected by NTP
// mid-form, to timezone changes, and to any clock skew between the phone and
// the server. Date.now() would let an NTP correction produce a NEGATIVE
// elapsed time and reject a real driver.
export function monotonicNow() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

// Elapsed seconds between two monotonicNow() readings. Both readings come from
// the same clock on the same device, so skew cancels out and we can send the
// DIFFERENCE rather than two timestamps the server would have to reconcile
// against its own clock.
export function elapsedSeconds(startedAt, now) {
  if (typeof startedAt !== 'number' || !isFinite(startedAt)) return null;
  const end = typeof now === 'number' ? now : monotonicNow();
  const seconds = (end - startedAt) / 1000;
  if (!isFinite(seconds)) return null;
  // Rounded down to whole seconds: the server compares against a whole-second
  // floor, and sending 2.9998 invites a float-comparison argument.
  return Math.floor(seconds);
}
